import React from 'react';

import { InfoOutlined as InfoIcon } from '@mui/icons-material';
import { Alert, alertClasses, Box, Grid, Typography } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { Field, Form } from '@hrlogics/react-components/form';

import { appRoutes } from '@clearverify/constants';
import { SimpleAnyObject } from '@clearverify/types';
import { validateWithSchema } from '@clearverify/utils';

import { useGetIsEmailFreeLazyQuery, useSendEmailConfirmationCodeMutation } from 'src/api';
import { Button, Link } from 'src/components/common';
import { PasswordField, PhoneField, TextField } from 'src/components/finalForm';
import { SignupVerifierFormStepId } from 'src/constants/auth';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useAuthSignupVerifier } from 'src/hooks/auth';
import { useModalV2 } from 'src/hooks/shared';
import { VerifierInviteResentModal } from 'src/modals/auth';
import { SxProp } from 'src/types/mui';

import { InitialStepFormFields, InitialStepFormValues } from '../types';
import { initialStepFormValidationSchema } from '../utils';

import { emailFreeErrorMessage } from './constants';

const DEFAULT_DEBOUNCE = 600;

export const InitialStepVerifierForm: React.FC = () => {
  const { createApolloContext } = useNotification();

  const { updateFormData, goToStep } = useAuthSignupVerifier();
  const { openModal, closeModal } = useModalV2();
  const [sendEmailConfirmationCode] = useSendEmailConfirmationCodeMutation({
    context: createApolloContext(),
  });
  const [getIsEmailFree] = useGetIsEmailFreeLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [isValidating, setIsValidating] = React.useState(false);

  // The modals provider outlives this page, so a browser Back would otherwise leave the
  // non-dismissible modal stranded on top of whatever renders next.
  React.useEffect(() => () => closeModal(ModalId.VerifierInviteResent), [closeModal]);

  const handleSubmit = React.useCallback(
    async (formValues: InitialStepFormValues) => {
      try {
        const { data } = await sendEmailConfirmationCode({
          variables: {
            email: formValues.email,
          },
        });

        if (data?.sendEmailConfirmationCode.pendingInviteResent) {
          openModal(VerifierInviteResentModal, { id: ModalId.VerifierInviteResent });
          return;
        }

        updateFormData(formValues);
        goToStep(SignupVerifierFormStepId.emailConfirmation);
      } catch (error) {
        console.error(error);
      }
    },
    [goToStep, openModal, sendEmailConfirmationCode, updateFormData],
  );

  const debouncedIsEmailFree = useDebouncedCallback(getIsEmailFree, DEFAULT_DEBOUNCE, {
    trailing: true,
    leading: true,
  });

  const handleCustomValidate = React.useCallback(
    async (formValues: SimpleAnyObject) => {
      setIsValidating(true);
      const result = await validateWithSchema(initialStepFormValidationSchema, formValues, {
        context: { getIsEmailFree: debouncedIsEmailFree },
      });
      setIsValidating(false);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedIsEmailFree, getIsEmailFree],
  );

  return (
    <React.Fragment>
      <Typography variant="h5">Sign up for Clear Verify</Typography>
      <Typography variant="body1" color="text.secondary" mt={1}>
        Already have an account? <Link to={appRoutes.auth.login}>Log in</Link>
      </Typography>
      <Form<InitialStepFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{
          valid: true,
          submitting: true,
          errors: true,
        }}
        validate={handleCustomValidate}
        containerProps={{ mt: 4 }}
      >
        {({ submitting, valid, errors }) => {
          return (
            <React.Fragment>
              {errors?.email === emailFreeErrorMessage && (
                <Alert variant="outlined" icon={<InfoIcon fontSize="inherit" />} sx={alertStyles}>
                  A corporate email is required for signing up to the platform. If you do not have
                  one, contact support for assistance.
                </Alert>
              )}
              <Grid container spacing={1.5}>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.firstName}
                    label="First Name"
                    slotProps={{ input: { autoComplete: 'given-name' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.lastName}
                    label="Last Name"
                    slotProps={{ input: { autoComplete: 'family-name' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={TextField}
                    name={InitialStepFormFields.email}
                    label="Email"
                    slotProps={{ input: { autoComplete: 'email' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PhoneField}
                    name={InitialStepFormFields.phoneNumber}
                    label="Phone Number"
                    slotProps={{ input: { autoComplete: 'off' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PasswordField}
                    name={InitialStepFormFields.password}
                    label="Password"
                    slotProps={{ input: { autoComplete: 'new-password' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>

                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <Field
                    component={PasswordField}
                    name={InitialStepFormFields.passwordConfirm}
                    label="Confirm Password"
                    slotProps={{ input: { autoComplete: 'off' } }}
                    fullWidth
                    required
                    disabled={submitting}
                  />
                </Grid>
              </Grid>
              <Box display="flex" justifyContent="center" mt={4}>
                <Button
                  type="submit"
                  disabled={submitting || !valid || isValidating}
                  loading={submitting}
                  color="primary"
                  size="large"
                  variant="contained"
                >
                  Sign up
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </React.Fragment>
  );
};

const initialValues: Partial<InitialStepFormValues> = {};

const alertStyles: SxProp = {
  mb: 4,
  color: '#000',
  borderColor: '#D32F2F',
  [`& .${alertClasses.icon}`]: {
    color: '#D32F2F',
  },
  [`& .${alertClasses.action}`]: {
    alignItems: 'center',
    padding: 0,
  },
};
